/**
 * The shell's stored record in local storage.
 *
 * `scratch` and `shared` name org entities: the selected camera, the agent being edited, the
 * cameras of a filter. After an org switch they name nothing, and an id that resolves to nothing
 * renders as an empty panel. The other fields belong to this browser: the widths and the panels
 * that the user keeps open. `clearOrgScopedPanelState` divides the record on that line.
 */

import { isPanelId } from '@/app/panelIds';
import { URL_PROP_KEYS } from '@/app/panelUrl';
import { getPanelEntry } from '@/app/registry';
import { CHAT_DEFAULT, clampChatWidth } from '@/app/shell-v3/chatWidth';
import { clampNavWidth, NAV_MIN } from '@/app/shell-v3/navResize';
import { isPlainObject, localStore } from '@/lib/webStorage';
import type { PanelLocalProps, PersistedShared, ShellRecord, UserWidths } from '@/types/panels';
import { MAX_PANEL_WIDTH } from '@/types/panels';

export const STORAGE_KEY = 'vms.panels.v3'; //< Also cleared by AppErrorBoundary's reset.
const STORAGE_SCHEMA_VERSION = 2;

export const DEFAULT_SHELL_RECORD: ShellRecord = {
    // Nav-rail button width in px. Defaults to icon-only: the rail is a
    // permanent fixture, so the default should cost the stage as little width
    // as possible. Labels are one drag away, and the tooltips cover discovery
    // until then.
    navWidth: NAV_MIN,
    // AI chat drawer width in px. It carries its own size outside the stage.
    chatWidth: CHAT_DEFAULT,
    openPanels: [], // panel ids
    scratch: {},
    focusedId: null,
    // Per-panel manual width overrides in exact pixels, or MAX_PANEL_WIDTH from
    // Expand, keyed by panel id. Local only — shared links do not carry personal sizes.
    userWidths: {},
    shared: {},
};

// The URL props mirrored into storage. A returning user starts at live, so the archive position
// `t` stays in the URL.
const PERSISTED_SHARED_KEYS = ['cameraId', 'layoutId'];

/**
 * The persisted shared props that the URL sets. Absent from the URL is not "cleared": cameraId
 * drops out of the URL whenever no open panel declares it (the cameras list alone closed, say),
 * and that must not wipe the remembered camera.
 */
export function readPersistedShared(searchParams: URLSearchParams): PersistedShared {
    const shared: PersistedShared = {};
    for (const key of PERSISTED_SHARED_KEYS) {
        const value = searchParams.get(key);
        if (value) {
            shared[key] = value;
        }
    }
    return shared;
}

/** The stored record, or null when there is none this build can read. */
function readStorage(): unknown {
    const parsedValue = localStore.readJson(STORAGE_KEY);
    if (!isPlainObject(parsedValue)) {
        return null;
    }
    // A record stamped by a schema this build has no migration for is not a
    // record at all; `migrate` would read v3's fields as v2's.
    if (parsedValue.v !== 1 && parsedValue.v !== STORAGE_SCHEMA_VERSION) {
        return null;
    }
    return parsedValue;
}

export function writeShellRecord(record: ShellRecord): void {
    localStore.writeJson(STORAGE_KEY, { v: STORAGE_SCHEMA_VERSION, ...record });
}

/**
 * Rebuild the persisted shape field-by-field from whatever is in storage. Runs as each store
 * starts, so it must never throw: a malformed but version-matching value would white-screen every
 * reload (the bad value is never rewritten) until storage was cleared by hand.
 */
export function migrate(storedValue: unknown): ShellRecord | null {
    if (!isPlainObject(storedValue)) {
        return null;
    }
    const rawOpen: unknown[] = Array.isArray(storedValue.openPanels) ? storedValue.openPanels : [];
    const localProps: PanelLocalProps = {};
    if (isPlainObject(storedValue.scratch)) {
        for (const [id, props] of Object.entries(storedValue.scratch)) {
            if (isPanelId(id) && isPlainObject(props)) {
                // A URL prop kept here would stand in for the URL leaving it out.
                localProps[id] = Object.fromEntries(Object.entries(props)
                    .filter(([key]) => !URL_PROP_KEYS.includes(key)));
            }
        }
    }
    // V1 stored viewport fractions; their original viewport is unknowable, so
    // preserve the rest of the record and start pixel overrides fresh. V2 keeps
    // only finite positive pixel widths so one malformed value cannot poison a
    // row allocation — and none at all for a `fill` panel, which is whatever the
    // row leaves over. Older builds saved one for the live view; drop it rather
    // than carry a number nothing reads. Expand's MAX_PANEL_WIDTH is kept for any
    // panel: it puts even a fill panel ahead of the others.
    const userWidths: UserWidths = {};
    if (storedValue.v === STORAGE_SCHEMA_VERSION && isPlainObject(storedValue.userWidths)) {
        for (const [id, width] of Object.entries(storedValue.userWidths)) {
            if (width === MAX_PANEL_WIDTH) {
                userWidths[id] = width;
            } else if (typeof width === 'number' && Number.isFinite(width) && width > 0 &&
                !getPanelEntry(id)?.config.width.fill) {
                userWidths[id] = width;
            }
        }
    }
    const shared: PersistedShared = {};
    if (isPlainObject(storedValue.shared)) {
        for (const key of PERSISTED_SHARED_KEYS) {
            const value = storedValue.shared[key];
            if (typeof value === 'string' && value) {
                shared[key] = value;
            }
        }
    }
    return {
        shared,
        navWidth: typeof storedValue.navWidth === 'number' && Number.isFinite(storedValue.navWidth)
            ? clampNavWidth(storedValue.navWidth) : DEFAULT_SHELL_RECORD.navWidth,
        chatWidth: typeof storedValue.chatWidth === 'number' &&
            Number.isFinite(storedValue.chatWidth)
            ? clampChatWidth(storedValue.chatWidth) : DEFAULT_SHELL_RECORD.chatWidth,
        openPanels: rawOpen.filter(isPanelId),
        scratch: localProps,
        // An off-stage panel is kept too: the phone puts it on the stage, and a
        // desktop row with no such panel in it anchors as if nothing held it.
        focusedId: isPanelId(storedValue.focusedId) ? storedValue.focusedId : null,
        userWidths,
    };
}

/** The stored record through `migrate`, or null when nothing readable is stored. */
export function readShellRecord(): ShellRecord | null {
    return migrate(readStorage());
}

/**
 * Drop the org-scoped half of the stored record, keeping the browser-scoped half — nav/chat
 * widths, per-panel width overrides, and which panels are open. Called by
 * auth/orgScope.ts before the active org changes; the reload that follows re-hydrates from what
 * is left.
 *
 * No-op when nothing is stored. The remaining fields are run through `migrate` first, so a record
 * written by an older schema is normalized exactly as a store's seed would normalize it rather
 * than being rewritten under a version stamp it doesn't match.
 */
export function clearOrgScopedPanelState(): void {
    const storedShellRecord = readShellRecord();
    if (!storedShellRecord) {
        return;
    }
    writeShellRecord({ ...storedShellRecord, scratch: {}, shared: {} });
}
